/**
 * Tabbed navigation for switching between Open, Closed, and All RMA views.
 * Each tab maps to a status_group value used for server-side filtering.
 */
import "./rma-list-tabs-bar.scss";
import { observer } from "mobx-react";
import useDashboardTranslation from "../../../../../../dashboard-translation/hooks/dashboard-translation-hook";
import { IRmaListTab, IRmaTabCounts } from "../../../../../../services/api/rma-list-service";
import { useCallback } from "react";
import rmaPagedListStore from "../../../stores/rma-tabs-paged-list-store";
import { trackManageReturnsListTabEvent } from "../../../../utils/manage-returns-list-mixpanel";
import TabButton from "./rma-list-tab-button/rma-list-tab-button";

interface ITabConfig {
    id: IRmaListTab;
    translationKey: string;
    countKey: keyof IRmaTabCounts;
}

interface IProps {
    isLoading: boolean;
}

const STATUS_GROUP_TABS: ITabConfig[] = [
    { id: IRmaListTab.STATUS_GROUP_OPEN, translationKey: "OPEN_RMAS_TAB", countKey: "open" },
    { id: IRmaListTab.STATUS_GROUP_CLOSED, translationKey: "CLOSED_RMAS_TAB", countKey: "closed" },
    { id: IRmaListTab.STATUS_GROUP_ALL, translationKey: "ALL_RMAS_TAB", countKey: "all" },
];

const RmaListTabsBar: React.FC<IProps> = observer(({ isLoading }) => {
    const { getTranslation } = useDashboardTranslation();
    const scope = "dashboard-returns-page";
    const rmaListFiltersStore = rmaPagedListStore.rmaListFiltersStore;

    const onTabChange = useCallback(
        (tab: IRmaListTab) => {
            if (tab === rmaListFiltersStore.currentTab) {
                return;
            }
            trackManageReturnsListTabEvent(tab);
            rmaListFiltersStore.setCurrentTab(tab);
            rmaPagedListStore.bulkSelectionStore.clear();
            rmaPagedListStore.loadPage(1);
        },
        [rmaListFiltersStore],
    );

    const tabCounts = rmaPagedListStore.tabCounts;

    return (
        <div className="rma-list-tabs-bar" role="tablist">
            {STATUS_GROUP_TABS.map((tab) => {
                const label = getTranslation(tab.translationKey, scope);
                const count = tabCounts ? tabCounts[tab.countKey] : undefined;
                return (
                    <TabButton
                        key={tab.id}
                        tabId={tab.id}
                        isActive={rmaListFiltersStore.currentTab === tab.id}
                        disabled={isLoading}
                        onSelect={onTabChange}
                    >
                        {count === undefined ? label : `${label} (${count})`}
                    </TabButton>
                );
            })}
        </div>
    );
});

export default RmaListTabsBar;
